// Copyright 2026 L3montree GmbH and the DevGuard Contributors.
// SPDX-License-Identifier: 	AGPL-3.0-or-later

//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     https://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
"use client";

import type { OrganizationDTO } from "@/types/dto";
import { truncateMiddle } from "@/utils/common";
import { ChevronUpDownIcon } from "@heroicons/react/24/outline";
import { Loader2, PlusIcon } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useConfig } from "../context/ConfigContext";
import { useSession } from "../context/SessionContext";
import { useActiveOrg } from "../hooks/useActiveOrg";
import GitProviderIcon from "./GitProviderIcon";
import { Badge } from "./ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useOrgSync } from "../hooks/useOrgSync";
import { useLocalStorage } from "@/hooks/useLocalStorage";

const activeOrgName = (name: string, slug: string) => {
  if (slug === "@opencode") {
    return "openCode";
  }
  if (slug === "@gitlab") {
    return "GitLab";
  }
  return name;
};
const OrganizationDropDown = () => {
  const orgs = useSession().organizations;
  const router = useRouter();
  const instanceSettings = useConfig();

  const [lastActiveOrg] = useLocalStorage("lastActiveOrg");

  let activeOrg = useActiveOrg() as OrganizationDTO | null;
  if (!activeOrg && lastActiveOrg) {
    activeOrg = orgs.find((o) => o.slug === lastActiveOrg) || null;
  } else if (!activeOrg && orgs.length > 0) {
    activeOrg = orgs[0];
  }

  const handleActiveOrgChange = (slug: string) => () => {
    // redirect to the new slug
    const org = orgs.find((o) => o.slug === slug);
    if (org) {
      router.push(`/${org.slug}`);
    }
  };

  const orgSyncRunning = useOrgSync();

  const handleNavigateToSetupOrg = () => {
    router.push(`/setup`);
  };

  return (
    <>
      <div
        data-tour="org-switcher"
        className="flex w-full flex-row gap-2 items-center justify-between"
      >
        {activeOrg && (
          <div className="flex flex-row items-center gap-1 text-ellipsis">
            <div className="flex flex-col gap-0 ">
              <Link href={`/${activeOrg?.slug}`}>
                <span className="line-clamp-1 gap-1 inline-flex items-center  truncate text-ellipsis text-left text-lg font-display font-semibold text-header-foreground">
                  {truncateMiddle(
                    activeOrgName(activeOrg.name, activeOrg.slug),
                  )}{" "}
                  {!activeOrg.externalEntityProviderId && (
                    <Badge
                      className="!text-header-foreground ml-2"
                      variant={"outline"}
                    >
                      Organization
                    </Badge>
                  )}
                </span>
              </Link>
            </div>
          </div>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger
            data-testid="org-switcher-dropdown"
            className="cursor-pointer rounded-lg focus:ring py-2 px-1 text-header-foreground transition-all hover:bg-white/10"
          >
            <ChevronUpDownIcon className="block h-7 w-7 p-1" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-48">
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Organizations
              </DropdownMenuLabel>
              {orgSyncRunning && (
                <DropdownMenuItem disabled>
                  <div className="flex flex-row items-center gap-2">
                    <span className="text-sm">Syncing...</span>
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                </DropdownMenuItem>
              )}
              {orgs.length !== 0 && (
                <>
                  {orgs
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((o) => (
                      <DropdownMenuItem
                        className="cursor-pointer"
                        key={o.id}
                        onClick={handleActiveOrgChange(o.slug)}
                      >
                        <div className="mr-2 flex  items-center justify-center rounded-md border bg-background p-1">
                          <GitProviderIcon
                            externalEntityProviderIdOrRepositoryId={
                              o.externalEntityProviderId
                            }
                          />
                        </div>
                        {truncateMiddle(o.name, 35)}
                      </DropdownMenuItem>
                    ))}
                </>
              )}
            </DropdownMenuGroup>
            {!instanceSettings?.singleOrganizationMode && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="cursor-pointer"
                  data-testid="create-new-organization-button"
                  onClick={handleNavigateToSetupOrg}
                >
                  <div className="mr-2 flex items-center justify-center rounded-md border bg-background p-1">
                    <PlusIcon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  Create Organization
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
};

export default OrganizationDropDown;
